import { useState, type CSSProperties, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { LOADER_STATUS_LABELS, type LoaderRevDto, type LoaderRevLoaderDto } from '@bhops/shared';
import { loaderRevsApi } from '@/api/loaders';
import { ecnsApi } from '@/api/ecns';
import { extractApiError } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import { useIsMobile } from '@/lib/responsive';
// 추가수정 2차(26-07-27) 정정 — '오늘'(KST)은 공용 포맷터 하나만 쓴다.
import { kstTodayYmd } from '@/lib/format-date';
import { canEditMfg, canHardDeleteCatalog } from '@/permissions';
import { Button, Input, Field, Textarea, FormError } from '@/components/Form';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Icon } from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';
import { loaderRevUploadsApi } from '@/api/loaderRevUploads';
import { LoaderRevUploadModal } from './LoaderRevUploadModal';
import { kstYmd } from '@/lib/format-date';

// 로더 개조(로더판 ECN) 카탈로그 + 일괄 적용 + 소급 대상 워크리스트 + 격자 보기.
//   개조 하나를 여러 로더에 한꺼번에 적용하고, 아직 안 된 로더(소급 대상)를 봅니다.
// 경로: /manufacturing/loader-revs.
//
// 개선계획 §1 — 종전의 "소급 적용(appliedRetroactively)" 체크박스는 어떤 로직도
// 읽지 않는 dead flag 였다. 실제로 동작하는 [신규 자동 적용] 스위치와 나란히
// 두면 운영자가 둘을 혼동하므로 **입력 UI 에서만 제거**했다(컬럼/기존 데이터는
// 그대로 보존). 기존 로더 소급은 아래 "미적용(소급 대상)" 일괄 적용이 담당한다.

// 자동 적용은 앞으로 등록할 로더에만 걸린다(오너 결정 ①A).
const AUTO_APPLY_HINT = (
  <>
    <strong>새 로더 등록 창</strong>에서 미리 체크된 상태로 나옵니다.
    {'\n'}이미 등록된 로더에는 적용되지 않습니다.
  </>
);

export function LoaderRevsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role ? canEditMfg(role) : false;
  const isMobile = useIsMobile();

  const [showMatrix, setShowMatrix] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  // 대장 업로드(2026-09-04) — 제조팀 엑셀을 콘솔에 반영하는 입구.
  const [uploadOpen, setUploadOpen] = useState(false);

  const revsQ = useQuery({
    queryKey: ['loader-revs', { includeArchived }],
    queryFn: () => loaderRevsApi.list(includeArchived ? 'all' : undefined),
    staleTime: 30_000,
  });

  const revs = revsQ.data?.items ?? [];

  return (
    <div style={pageWrap}>
      <div>
        <Link to="/manufacturing/loaders" style={backLink}>
          <Icon name="arrow-right" size={14} aria-hidden style={{ transform: 'rotate(180deg)' }} />
          로더 목록으로
        </Link>
      </div>

      <header style={headerWrap}>
        <div>
          <h1 style={titleStyle}>로더 개조 (E01~E06)</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* 변경점(ECN) 카탈로그와 같은 보관/복원 동선 — 보관된 개조는 기본 숨김. */}
          <label style={inlineCheck}>
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            보관 포함
          </label>
          {canEdit && (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setUploadOpen(true)}
              data-testid="loader-ledger-upload"
            >
              대장 업로드
            </Button>
          )}
          {!isMobile ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowMatrix((v) => !v)}
              data-testid="loader-matrix-toggle"
            >
              <Icon name="layers" size={14} aria-hidden />
              {showMatrix ? '격자 닫기' : '격자 보기'}
            </Button>
          ) : (
            <span style={mobileNote}>격자 보기는 PC에서만 됩니다</span>
          )}
        </div>
      </header>

      <LoaderRevUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />

      {showMatrix && !isMobile && <MatrixSection />}

      {canEdit && <CreateRevCard />}

      {revsQ.isLoading ? (
        <div style={loadingStyle}>불러오는 중…</div>
      ) : revsQ.error ? (
        <div style={errorStyle}>{extractApiError(revsQ.error)}</div>
      ) : revs.length === 0 ? (
        <div style={emptyBlock}>등록된 개조가 없습니다.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {revs.map((rev) => (
            <RevRow key={rev.id} rev={rev} canEdit={canEdit} />
          ))}
        </div>
      )}

      <LedgerUploadHistory />
    </div>
  );
}

// ── 대장 업로드 이력 ─────────────────────────────
// 요구 2 — 올린 원본을 다시 내려받는다. 확정·취소를 모두 남긴다(무엇을 올렸다 접었는지도
// 기록이다). 이력은 감사 성격이라 지우는 버튼을 두지 않는다.
function LedgerUploadHistory() {
  const q = useQuery({
    queryKey: ['loader-rev-uploads'],
    queryFn: () => loaderRevUploadsApi.list(),
    staleTime: 30_000,
  });
  const items = q.data?.items ?? [];
  if (q.isLoading) return <div style={panelLoading}>업로드 이력 불러오는 중…</div>;
  if (q.error) return <div style={errorStyle}>{extractApiError(q.error)}</div>;
  if (items.length === 0) return null;

  return (
    <div style={card}>
      <h3 style={cardTitle}>대장 업로드 이력</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={historyTable}>
          <thead>
            <tr>
              <th style={historyTh}>파일</th>
              <th style={historyTh}>기준일</th>
              <th style={historyTh}>올린 사람</th>
              <th style={historyTh}>올린 시각</th>
              <th style={historyTh}>결과</th>
              <th style={historyTh}>반영</th>
              <th style={historyTh} />
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid="ledger-history-row">
                <td style={{ ...historyTd, fontFamily: 'var(--font-mono)' }}>{it.fileName}</td>
                <td style={historyTd}>{it.baseDate}</td>
                <td style={historyTd}>{it.uploadedByName ?? '–'}</td>
                <td style={historyTd}>{kstYmd(it.uploadedAt) ?? '–'}</td>
                <td style={historyTd}>
                  {it.status === 'CONFIRMED' ? (
                    <span style={{ color: 'var(--fg-brand)', fontWeight: 700 }}>
                      확정 {it.confirmedByName ? `· ${it.confirmedByName}` : ''}
                    </span>
                  ) : it.status === 'CANCELLED' ? (
                    <span style={{ color: 'var(--fg-muted)' }}>취소</span>
                  ) : it.status === 'PREVIEWED' ? (
                    <span style={{ color: 'var(--fg-muted)' }}>확인 대기</span>
                  ) : (
                    <span style={{ color: 'var(--fg-muted)' }}>올리는 중</span>
                  )}
                </td>
                <td style={historyTd}>
                  {it.status === 'CONFIRMED'
                    ? `신규 ${it.counts.created}, 변경 ${it.counts.changed}, 충돌 ${it.counts.conflicted}`
                    : '–'}
                </td>
                <td style={historyTd}>
                  {it.downloadUrl ? (
                    <a href={it.downloadUrl} style={loaderSerialLink} rel="noreferrer">
                      원본 내려받기
                    </a>
                  ) : (
                    <span style={{ color: 'var(--fg-muted)' }}>–</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const historyTable: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };
const historyTh: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: 'var(--fg-muted)',
  fontWeight: 600,
  padding: '6px 8px',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};
const historyTd: CSSProperties = {
  padding: '7px 8px',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};

// ── 개조 추가(인라인 생성 카드) ─────────────────────────────
function CreateRevCard() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [seq, setSeq] = useState('');
  const [effectiveAt, setEffectiveAt] = useState('');
  const [autoApply, setAutoApply] = useState(false);
  const [ecnIds, setEcnIds] = useState<string[]>([]);

  const createM = useMutation({
    mutationFn: () =>
      loaderRevsApi.create({
        code: code.trim(),
        displayName: displayName.trim(),
        description: description.trim() || undefined,
        seq: seq.trim() ? Number(seq) : undefined,
        effectiveAt: effectiveAt || undefined,
        autoApplyToNewLoaders: autoApply,
        ecnIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loader-revs'] });
      reset();
      setOpen(false);
    },
  });

  function reset(): void {
    setCode('');
    setDisplayName('');
    setDescription('');
    setSeq('');
    setEffectiveAt('');
    setAutoApply(false);
    setEcnIds([]);
  }

  if (!open) {
    return (
      <div>
        <Button type="button" onClick={() => setOpen(true)} data-testid="loader-rev-create-open">
          <Icon name="plus" size={14} aria-hidden />
          개조 추가
        </Button>
      </div>
    );
  }

  const canSubmit = code.trim() !== '' && displayName.trim() !== '';

  return (
    <div style={card}>
      <div style={cardHead}>
        <h3 style={cardTitle}>새 개조 추가</h3>
        <button type="button" onClick={() => setOpen(false)} style={iconBtn} aria-label="닫기">
          <Icon name="x" size={16} aria-hidden />
        </button>
      </div>
      <div style={formGrid}>
        <Field label="코드">
          <Input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="E01"
            data-testid="loader-rev-code"
          />
        </Field>
        <Field label="표시명">
          <Input
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="예: 그리퍼 강화"
            data-testid="loader-rev-name"
          />
        </Field>
        <Field label="순서(seq)">
          <Input
            type="number"
            value={seq}
            onChange={(e) => setSeq(e.target.value)}
            placeholder="1"
          />
        </Field>
        <Field label="적용 기준일">
          <Input type="date" value={effectiveAt} onChange={(e) => setEffectiveAt(e.target.value)} />
        </Field>
      </div>
      <Field label="설명 (선택)">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
      </Field>
      <EcnMultiSelect idPrefix="loader-rev-new" value={ecnIds} onChange={setEcnIds} />
      <AutoApplyToggle
        id="loader-rev-new-auto"
        checked={autoApply}
        onChange={setAutoApply}
        testId="loader-rev-create-auto-apply"
      />

      {createM.isError && <FormError>{extractApiError(createM.error)}</FormError>}

      <div style={formActions}>
        <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
          취소
        </Button>
        <Button
          type="button"
          onClick={() => createM.mutate()}
          disabled={!canSubmit || createM.isPending}
          data-testid="loader-rev-create-submit"
        >
          {createM.isPending ? '저장 중…' : '추가'}
        </Button>
      </div>
    </div>
  );
}

// ── 연결 변경점(ECN) 선택 ─────────────────────────────
// 종전에는 raw cuid 를 쉼표로 이어 붙이는 텍스트 입력이라 실사용이 불가능했다.
// 카탈로그를 그대로 불러와 검색 + 체크박스로 고른다(목록이 작아 클라이언트 필터).
function EcnMultiSelect({
  idPrefix,
  value,
  onChange,
}: {
  idPrefix: string;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [q, setQ] = useState('');
  const ecnsQ = useQuery({
    queryKey: ['ecns', { picker: 'loader-rev' }],
    queryFn: () => ecnsApi.list(),
    staleTime: 60_000,
  });

  const items = ecnsQ.data?.items ?? [];
  const needle = q.trim().toLowerCase();
  // 이미 고른 항목은 검색어와 무관하게 남겨 둔다 — 안 보이면 해제할 수 없다.
  const visible = needle
    ? items.filter(
        (e) =>
          value.includes(e.id) ||
          e.code.toLowerCase().includes(needle) ||
          e.description.toLowerCase().includes(needle),
      )
    : items;

  function toggle(id: string): void {
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  }

  return (
    <Field label={`연결 변경점 (선택${value.length > 0 ? `, ${value.length}개` : ''})`}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="설명 또는 코드로 검색"
          aria-label={`${idPrefix} 변경점 검색`}
        />
        {ecnsQ.isLoading ? (
          <div style={emptyRowSm}>불러오는 중…</div>
        ) : ecnsQ.error ? (
          <FormError>{extractApiError(ecnsQ.error)}</FormError>
        ) : visible.length === 0 ? (
          <div style={emptyRowSm}>검색 결과가 없습니다.</div>
        ) : (
          <div style={pickList} data-testid={`${idPrefix}-ecn-list`}>
            {visible.map((e) => (
              <label key={e.id} style={pickItem}>
                <input
                  type="checkbox"
                  checked={value.includes(e.id)}
                  onChange={() => toggle(e.id)}
                  data-testid={`${idPrefix}-ecn-${e.id}`}
                />
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--fg-brand)' }}>
                  {e.code}
                </span>
                <span style={{ color: 'var(--fg-muted)' }}>{e.description}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </Field>
  );
}

// [신규 자동 적용] 스위치 — 생성 폼·편집 모달·목록 행에서 같은 문구/툴팁.
function AutoApplyToggle({
  id,
  checked,
  onChange,
  disabled,
  testId,
}: {
  id: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <Tooltip content={AUTO_APPLY_HINT} focusable={false}>
      <label htmlFor={id} style={checkboxRow}>
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={testId}
        />
        신규 자동 적용
      </label>
    </Tooltip>
  );
}

// ── 카탈로그 한 줄(펼침) ─────────────────────────────
function RevRow({ rev, canEdit }: { rev: LoaderRevDto; canEdit: boolean }) {
  const qc = useQueryClient();
  const role = useAuthStore((s) => s.user?.role);
  const canHardDelete = role ? canHardDeleteCatalog(role) : false;
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmHardDelete, setConfirmHardDelete] = useState(false);

  function invalidate(): void {
    qc.invalidateQueries({ queryKey: ['loader-revs'] });
    qc.invalidateQueries({ queryKey: ['loader-matrix'] });
  }

  const archiveM = useMutation({
    mutationFn: () => loaderRevsApi.remove(rev.id),
    onSuccess: invalidate,
  });

  const restoreM = useMutation({
    mutationFn: () => loaderRevsApi.update(rev.id, { active: true }),
    onSuccess: invalidate,
  });

  const hardDeleteM = useMutation({
    mutationFn: () => loaderRevsApi.hardDelete(rev.id),
    onSuccess: () => {
      setConfirmHardDelete(false);
      invalidate();
    },
  });

  const autoApplyM = useMutation({
    mutationFn: (next: boolean) => loaderRevsApi.update(rev.id, { autoApplyToNewLoaders: next }),
    onSuccess: invalidate,
  });

  // 적용 이력이 남아 있으면 서버가 409 로 막는다 — 눌러보고 알게 하지 말고
  // 버튼 자체를 비활성 + 사유를 title 로.
  const blockedReason =
    rev.appliedLoaderCount > 0
      ? `적용 이력이 ${rev.appliedLoaderCount}건 있어 완전삭제할 수 없습니다. 먼저 적용을 해제해 주세요.`
      : null;

  return (
    <div style={card}>
      <div style={revHeadRow}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={codeMono}>{rev.code}</span>
          <span style={{ fontWeight: 600 }}>{rev.displayName}</span>
          <Tooltip
            content={
              <>
                <strong>
                  {rev.code} · {rev.displayName}
                </strong>
                {rev.description ? `\n${rev.description}` : ''}
                {'\n'}적용 {rev.appliedLoaderCount}대
              </>
            }
          >
            <span style={countPill}>적용 {rev.appliedLoaderCount}대</span>
          </Tooltip>
          {rev.ecnLinks.map((ecn) => (
            <Tooltip
              key={ecn.ecnId}
              content={
                <>
                  <strong>{ecn.code}</strong>
                  {'\n'}
                  {ecn.description}
                </>
              }
            >
              <span style={ecnChip}>{ecn.code}</span>
            </Tooltip>
          ))}
          {rev.autoApplyToNewLoaders && <span style={autoPill}>신규 자동 적용</span>}
          {!rev.active && <span style={inactivePill}>보관됨</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {canEdit && (
            <AutoApplyToggle
              id={`loader-rev-auto-${rev.id}`}
              checked={rev.autoApplyToNewLoaders}
              onChange={(next) => autoApplyM.mutate(next)}
              disabled={autoApplyM.isPending}
              testId={`loader-rev-auto-apply-${rev.id}`}
            />
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setExpanded((v) => !v)}
            data-testid={`loader-rev-toggle-${rev.id}`}
          >
            {expanded ? '접기' : '적용/미적용 보기'}
          </Button>
          {canEdit &&
            (rev.active ? (
              <Button
                type="button"
                variant="danger"
                onClick={() => archiveM.mutate()}
                disabled={archiveM.isPending}
                data-testid={`loader-rev-archive-${rev.id}`}
              >
                {archiveM.isPending ? '…' : '보관'}
              </Button>
            ) : (
              <Button
                type="button"
                variant="secondary"
                onClick={() => restoreM.mutate()}
                disabled={restoreM.isPending}
                data-testid={`loader-rev-restore-${rev.id}`}
              >
                {restoreM.isPending ? '…' : '복원'}
              </Button>
            ))}
          {canHardDelete && !rev.active && (
            <Button
              type="button"
              variant="danger"
              onClick={() => setConfirmHardDelete(true)}
              disabled={blockedReason !== null || hardDeleteM.isPending}
              title={blockedReason ?? '되돌릴 수 없습니다.'}
              data-testid={`loader-rev-hard-delete-${rev.id}`}
            >
              완전삭제
            </Button>
          )}
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              style={rowEditBtn}
              aria-label={`${rev.code} 편집`}
              title="개조 편집"
              data-testid={`loader-rev-edit-${rev.id}`}
            >
              <Icon name="pen" size={14} aria-hidden />
            </button>
          )}
        </div>
      </div>
      {rev.description && <div style={descText}>{rev.description}</div>}
      {(archiveM.isError || restoreM.isError || hardDeleteM.isError || autoApplyM.isError) && (
        <FormError>
          {extractApiError(
            archiveM.error ?? restoreM.error ?? hardDeleteM.error ?? autoApplyM.error,
          )}
        </FormError>
      )}
      {expanded && <RevLoadersPanel revId={rev.id} canEdit={canEdit} />}
      {editOpen && <RevEditModal rev={rev} onClose={() => setEditOpen(false)} />}
      <ConfirmDialog
        open={confirmHardDelete}
        title="개조 완전삭제"
        message={`${rev.code} · ${rev.displayName}\n\n완전삭제하면 되돌릴 수 없습니다. 계속할까요?`}
        confirmLabel="완전삭제"
        danger
        busy={hardDeleteM.isPending}
        onConfirm={() => hardDeleteM.mutate()}
        onCancel={() => setConfirmHardDelete(false)}
      />
    </div>
  );
}

// ── 개조(카탈로그) 편집 모달 — code 제외 필드 수정 ─────────────────────────
function RevEditModal({ rev, onClose }: { rev: LoaderRevDto; onClose: () => void }) {
  const qc = useQueryClient();
  const [displayName, setDisplayName] = useState(rev.displayName);
  const [description, setDescription] = useState(rev.description ?? '');
  const [seq, setSeq] = useState(String(rev.seq));
  const [effectiveAt, setEffectiveAt] = useState(
    rev.effectiveAt ? rev.effectiveAt.slice(0, 10) : '',
  );
  const [autoApply, setAutoApply] = useState(rev.autoApplyToNewLoaders);
  const [ecnIds, setEcnIds] = useState<string[]>(rev.ecnLinks.map((e) => e.ecnId));
  const [active, setActive] = useState(rev.active);

  const updateM = useMutation({
    mutationFn: () =>
      loaderRevsApi.update(rev.id, {
        displayName: displayName.trim() || undefined,
        description: description.trim() ? description.trim() : null,
        seq: seq.trim() !== '' && Number.isFinite(Number(seq)) ? Number(seq) : undefined,
        effectiveAt: effectiveAt ? `${effectiveAt}T00:00:00.000Z` : null,
        autoApplyToNewLoaders: autoApply,
        // 전체 교체(set) — 편집에서 연결 해제가 되게 하려면 항상 보내야 한다.
        ecnIds,
        active,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loader-revs'] });
      qc.invalidateQueries({ queryKey: ['loader-matrix'] });
      onClose();
    },
  });

  return (
    <Modal open onClose={onClose} title={`${rev.code} 개조 편집`} width={520}>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Field label="이름" htmlFor="rev-name">
          <Input
            id="rev-name"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </Field>
        <Field label="설명" htmlFor="rev-desc">
          <Textarea
            id="rev-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label="정렬 순서" htmlFor="rev-seq">
          <Input id="rev-seq" type="number" value={seq} onChange={(e) => setSeq(e.target.value)} />
        </Field>
        <Field label="적용일" htmlFor="rev-eff">
          <Input
            id="rev-eff"
            type="date"
            value={effectiveAt}
            onChange={(e) => setEffectiveAt(e.target.value)}
          />
        </Field>
        <EcnMultiSelect
          idPrefix={`loader-rev-edit-${rev.id}`}
          value={ecnIds}
          onChange={setEcnIds}
        />
        <AutoApplyToggle
          id={`loader-rev-edit-auto-${rev.id}`}
          checked={autoApply}
          onChange={setAutoApply}
          testId={`loader-rev-edit-auto-apply-${rev.id}`}
        />
        <label style={checkRow}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          활성
        </label>

        {updateM.isError && <FormError>{extractApiError(updateM.error)}</FormError>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="button" onClick={() => updateM.mutate()} disabled={updateM.isPending}>
            {updateM.isPending ? '저장 중…' : '저장'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── 적용/미적용 로더 패널 ─────────────────────────────
function RevLoadersPanel({ revId, canEdit }: { revId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['loader-rev-loaders', revId],
    queryFn: () => loaderRevsApi.listLoaders(revId),
    enabled: true,
    staleTime: 15_000,
  });

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [appliedAt, setAppliedAt] = useState(kstTodayYmd());

  const applyM = useMutation({
    mutationFn: () =>
      loaderRevsApi.apply(revId, {
        loaderUnitIds: Array.from(selected),
        // UTC 자정 — toYmd(UTC slice)와 round-trip(로컬자정은 KST 에서 하루 밀림).
        appliedAt: appliedAt ? `${appliedAt}T00:00:00.000Z` : undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loader-rev-loaders', revId] });
      qc.invalidateQueries({ queryKey: ['loader-revs'] });
      qc.invalidateQueries({ queryKey: ['loader-matrix'] });
      setSelected(new Set());
    },
  });

  const unapplyM = useMutation({
    mutationFn: (loaderUnitId: string) => loaderRevsApi.unapply(revId, loaderUnitId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loader-rev-loaders', revId] });
      qc.invalidateQueries({ queryKey: ['loader-revs'] });
      qc.invalidateQueries({ queryKey: ['loader-matrix'] });
    },
  });

  const applied = q.data?.applied ?? [];
  const missing = q.data?.missing ?? [];

  function toggle(id: string): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const allSelected = missing.length > 0 && selected.size === missing.length;
  function toggleAll(): void {
    setSelected(allSelected ? new Set() : new Set(missing.map((m) => m.loaderUnitId)));
  }

  if (q.isLoading) return <div style={panelLoading}>불러오는 중…</div>;
  if (q.error) return <div style={errorStyle}>{extractApiError(q.error)}</div>;

  return (
    <div style={panelWrap}>
      {(applyM.isError || unapplyM.isError) && (
        <FormError>{extractApiError(applyM.error ?? unapplyM.error)}</FormError>
      )}

      <div style={panelCols}>
        {/* 적용된 로더 */}
        <div style={panelCol}>
          <div style={colHead}>적용된 로더 ({applied.length})</div>
          {applied.length === 0 ? (
            <div style={emptyRowSm}>없음</div>
          ) : (
            <div style={loaderList}>
              {applied.map((l) => (
                <LoaderRow
                  key={l.loaderUnitId}
                  loader={l}
                  right={
                    canEdit ? (
                      <button
                        type="button"
                        onClick={() => unapplyM.mutate(l.loaderUnitId)}
                        disabled={unapplyM.isPending}
                        style={unapplyBtn}
                        data-testid={`loader-rev-unapply-${l.loaderUnitId}`}
                      >
                        해제
                      </button>
                    ) : null
                  }
                />
              ))}
            </div>
          )}
        </div>

        {/* 미적용(소급 대상) */}
        <div style={panelCol}>
          <div style={colHead}>아직 적용되지 않은 로더 ({missing.length})</div>
          {canEdit && missing.length > 0 && (
            <div style={applyBar}>
              <label style={selectAllRow}>
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                전체 선택
              </label>
              <Input
                type="date"
                value={appliedAt}
                onChange={(e) => setAppliedAt(e.target.value)}
                aria-label="적용일"
                style={{ maxWidth: 150 }}
              />
              <Button
                type="button"
                onClick={() => applyM.mutate()}
                disabled={selected.size === 0 || applyM.isPending}
                data-testid={`loader-rev-apply-${revId}`}
              >
                {applyM.isPending ? '적용 중…' : `선택 적용 (${selected.size})`}
              </Button>
            </div>
          )}
          {missing.length === 0 ? (
            <div style={emptyRowSm}>소급 대상이 없습니다.</div>
          ) : (
            <div style={loaderList}>
              {missing.map((l) => (
                <LoaderRow
                  key={l.loaderUnitId}
                  loader={l}
                  right={
                    canEdit ? (
                      <input
                        type="checkbox"
                        checked={selected.has(l.loaderUnitId)}
                        onChange={() => toggle(l.loaderUnitId)}
                        data-testid={`loader-rev-pick-${l.loaderUnitId}`}
                      />
                    ) : null
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function LoaderRow({ loader, right }: { loader: LoaderRevLoaderDto; right: ReactNode }) {
  return (
    <div style={loaderRow}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <Link to={`/manufacturing/loaders/${loader.loaderUnitId}`} style={loaderSerialLink}>
          {loader.serial}
        </Link>
        <span style={loaderSub}>
          {LOADER_STATUS_LABELS[loader.status]}
          {loader.currentRobotSerial ? ` · 장착: ${loader.currentRobotSerial}` : ''}
        </span>
      </div>
      {right}
    </div>
  );
}

// ── 격자(매트릭스) ─────────────────────────────
function MatrixSection() {
  const q = useQuery({
    queryKey: ['loader-matrix'],
    queryFn: () => loaderRevsApi.matrix(),
    staleTime: 30_000,
  });

  if (q.isLoading) return <div style={panelLoading}>격자 불러오는 중…</div>;
  if (q.error) return <div style={errorStyle}>{extractApiError(q.error)}</div>;

  const revs = q.data?.revs ?? [];
  const loaders = q.data?.loaders ?? [];

  if (revs.length === 0 || loaders.length === 0) {
    return <div style={emptyBlock}>격자에 표시할 데이터가 없습니다.</div>;
  }

  return (
    <div style={card}>
      <h3 style={cardTitle}>개조 적용 격자</h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={matrixTable}>
          <thead>
            <tr>
              <th style={{ ...matrixTh, textAlign: 'left' }}>로더</th>
              {revs.map((r) => (
                <th key={r.id} style={matrixTh} title={r.displayName}>
                  {r.code}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loaders.map((l) => (
              <tr key={l.loaderUnitId}>
                <td style={matrixTdSerial}>
                  <Link to={`/manufacturing/loaders/${l.loaderUnitId}`} style={loaderSerialLink}>
                    {l.serial}
                  </Link>
                </td>
                {revs.map((r) => (
                  <td key={r.id} style={matrixTd}>
                    {l.appliedRevCodes.includes(r.code) ? '○' : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// 추가수정 2차(26-07-27) 정정 — 로컬 todayYmd 제거. date input 기본값의
// '오늘'은 공용 kstTodayYmd 하나만 쓴다(로컬 재구현 금지).

// ── 스타일 ─────────────────────────────
// 로더 페이지 공통 컨테이너 — LoadersListPage/EcnCatalogPage 와 동일하게 전폭·좌측
// 정렬(중앙 정렬·자체 padding·maxWidth 없음; 여백은 AppShell main 이 제공).
const pageWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const backLink: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 13,
  color: 'var(--fg-muted)',
};
const headerWrap: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};
const titleStyle: CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  margin: 0,
  color: 'var(--fg-default)',
};
const inlineCheck: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};
const mobileNote: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  padding: '6px 10px',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-btn)',
};
const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 14,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};
const cardHead: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};
const cardTitle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  margin: 0,
  color: 'var(--fg-default)',
};
const iconBtn: CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--fg-muted)',
  padding: 4,
  display: 'inline-flex',
};
const formGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 10,
};
const checkboxRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 13,
  color: 'var(--fg-default)',
};
const formActions: CSSProperties = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
};
const revHeadRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  flexWrap: 'wrap',
};
const codeMono: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontWeight: 700,
  fontSize: 14,
  color: 'var(--fg-brand)',
};
const countPill: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--bg-sunken)',
  color: 'var(--fg-muted)',
};
const ecnChip: CSSProperties = {
  fontSize: 11,
  fontFamily: 'var(--font-mono)',
  padding: '2px 6px',
  borderRadius: 'var(--r-btn)',
  background: 'var(--brand-subtle)',
  color: 'var(--fg-brand)',
};
const autoPill: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--bg-success-subtle)',
  color: 'var(--fg-on-success-tint)',
};
const pickList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 8,
  maxHeight: 180,
  overflowY: 'auto',
};
const pickItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-default)',
  cursor: 'pointer',
};
const inactivePill: CSSProperties = {
  fontSize: 11,
  padding: '2px 8px',
  borderRadius: 999,
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
};
const descText: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
};
const rowEditBtn: CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--fg-muted)',
  padding: 4,
  lineHeight: 0,
  borderRadius: 'var(--r-btn)',
};
const checkRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  color: 'var(--fg-default)',
  margin: '4px 0',
  cursor: 'pointer',
};
const panelWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  paddingTop: 10,
  borderTop: '1px solid var(--border-subtle)',
};
const panelCols: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
  gap: 12,
};
const panelCol: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
const colHead: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--fg-default)',
};
const applyBar: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: 8,
  background: 'var(--bg-sunken)',
  borderRadius: 'var(--r-btn)',
};
const selectAllRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 12,
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};
const loaderList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  maxHeight: 280,
  overflow: 'auto',
};
const loaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '8px 10px',
  borderBottom: '1px solid var(--border-subtle)',
};
const loaderSerialLink: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontWeight: 600,
  fontSize: 13,
  color: 'var(--fg-brand)',
};
const loaderSub: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
};
const unapplyBtn: CSSProperties = {
  border: '1px solid var(--border-default)',
  background: 'transparent',
  borderRadius: 'var(--r-btn)',
  padding: '3px 10px',
  fontSize: 12,
  cursor: 'pointer',
  color: 'var(--fg-danger)',
};
const matrixTable: CSSProperties = {
  borderCollapse: 'collapse',
  fontSize: 12,
  minWidth: 400,
};
const matrixTh: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '2px solid var(--border-default)',
  fontFamily: 'var(--font-mono)',
  color: 'var(--fg-muted)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
};
const matrixTd: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  textAlign: 'center',
  color: 'var(--fg-brand)',
  fontWeight: 700,
};
const matrixTdSerial: CSSProperties = {
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};
const loadingStyle: CSSProperties = { padding: 40, textAlign: 'center', color: 'var(--fg-muted)' };
const panelLoading: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  fontSize: 12,
};
const errorStyle: CSSProperties = {
  padding: 16,
  color: 'var(--fg-danger)',
  background: 'var(--bg-danger-subtle)',
  borderRadius: 'var(--r-card)',
};
const emptyBlock: CSSProperties = {
  padding: 24,
  textAlign: 'center',
  color: 'var(--fg-muted)',
  border: '1px dashed var(--border-default)',
  borderRadius: 'var(--r-card)',
  fontSize: 13,
};
const emptyRowSm: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
};
